import "./style.scss";

import { MoreHorizOutlined } from "@mui/icons-material";
import type { PopoverOrigin } from "@mui/material";
import { ClickAwayListener, Menu, MenuItem, Tooltip } from "@mui/material";
import { IconButton } from "@mui/material";
import { FocusEventHandler, useRef, useState } from "react";

export interface ThreeDotMenuOptions {
    id: number | string;
    /**
     * Label of the option.
     */
    name: string | JSX.Element;
    /**
     * Icon that comes before label.
     */
    icon?: JSX.Element;
    /**
     * Icon placement, before or after the label
     */
    iconPosition?: "before" | "after";
    /**
     * Condition to hide the option.
     */
    hide?: boolean;
    /**
     * Action triggered when clicking on the button.
     */
    action: (arg?: any) => void;
    disabled?: boolean;
    /**
     * Add a tooltip to the action
     */
    tooltip?: string;
}

interface ThreeDotMenu {
    /**
     * Array with all options appearing when clicking on button.
     */
    options: ThreeDotMenuOptions[];
    /**
     * Extra styles for the button.
     */
    style?: React.CSSProperties;
    /**
     * Changes the default icon that comes before label.
     */
    menuIcon?: JSX.Element;
    /**
     * On click event for the button that opens the menu (works as an event listener if needed)
     */
    onMenuClick?: () => void;
    /**
     * Modify Mui anchorOrigin props
     */
    anchorOrigin?: PopoverOrigin;
}

/**
 * Menu button with Three Dots Icon.
 */
const ThreeDotMenu = ({ options, style = {}, menuIcon, onMenuClick, anchorOrigin }: ThreeDotMenu) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const menuRef = useRef(null);

    /**
     * Handles opening the menu
     * @param event Click on button
     */
    const handleMenuOpening = (event: React.MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        if (onMenuClick) onMenuClick();
    };

    const handleClose = (ev: any, option: ThreeDotMenuOptions) => {
        ev.stopPropagation();
        option.action();
        setAnchorEl(null);
    };

    const handleClickOutside = (event: any) => {
        if (event.relatedTarget === null) {
            event.stopPropagation();
            setAnchorEl(null);
        }
    };

    return (
        <div className="three-dot-menu">
            <IconButton className="icon-btn" style={style} onClick={handleMenuOpening}>
                {menuIcon || <MoreHorizOutlined />}
            </IconButton>
            <Menu
                ref={menuRef}
                id="menu"
                anchorEl={anchorEl}
                open={open}
                onClose={(e: any) => {
                    setAnchorEl(null);
                    e.stopPropagation();
                }}
                className="button-menu-menu"
                elevation={0}
                anchorOrigin={
                    anchorOrigin || {
                        vertical: "bottom",
                        horizontal: "right",
                    }
                }
                transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                }}
                onBlur={handleClickOutside}
            >
                {options.map((option, i) => {
                    const iconPosition = option?.iconPosition || "before";
                    return (
                        <Tooltip title={option?.tooltip} key={option.id}>
                            <div>
                                {/** This div prevents the cursor pointer event none of disabled, to show tooltip */}
                                <MenuItem
                                    onClick={(ev) => handleClose(ev, option)}
                                    disabled={option.disabled}
                                    style={{ display: option.hide ? "none" : "" }}
                                >
                                    {(iconPosition === "before" && option.icon) || <></>}
                                    {option.name}
                                    {(iconPosition === "after" && option.icon) || <></>}
                                </MenuItem>
                            </div>
                        </Tooltip>
                    );
                })}
            </Menu>
        </div>
    );
};

export default ThreeDotMenu;
